import { LayersApiConfiguration } from "@/env";
import type { LayerUnionWithAll } from "@/layers";
import type { CrossModelParameterSpecification, ModelSchema } from "@mm/api-layers.meteomatics.com";
import { APILayersApi, ModelsApi, StationsApi } from "@mm/api-layers.meteomatics.com";
import Logger from "@mm/logging";
import type { WeatherParamSearchOptions } from "@mm/weather-parameter-utils";
import { ModelSearchEngine } from "@mm/weather-parameter-utils/search-engines";
import { WeatherParamSearchParallel } from "@mm/weather-parameter-utils/search-engines/parallel";
import { useContext, useEffect, useState } from "react";
import { createContext } from "react";
import { PARAMETERS_DISABLED, PARAMETERS_ENABLED } from "./config";
import { STATION_MODEL_ID, STATION_MODEL_LABEL } from "./staticStationModels";
import {
  aargauRoadStationsParams,
  gotthardRoadStationsParams,
  nationalstrassenRoadStationsParams,
  nsnwRoadStationsParams,
  zentrasRoadStationsParams,
} from "./winterdienstParamList";
const logger = Logger.fromFilename(__filename);

const layersApi = new APILayersApi(LayersApiConfiguration);
const modelsApi = new ModelsApi(LayersApiConfiguration);
const stationApi = new StationsApi(LayersApiConfiguration);

export interface LayersApiResponses {
  /**
   * Contains a list of currently available general weather params
   */
  generalWeatherParams?: CrossModelParameterSpecification[];
  /**
   * Contains a list of all the available models(data sources) for general weather params
   */
  allSourceModelsGeneralWeatherParams?: ModelSchema[];
}

export const searchOptions: WeatherParamSearchOptions = {
  disabledFlags: PARAMETERS_DISABLED,
  enabledFlags: PARAMETERS_ENABLED,
  maxNumberOfResults: 5,
};

type SearchEngineKey =
  | "standard"
  | "stationObs"
  | "stationMos"
  | "stationAargau"
  | "stationNationalstrassen"
  | "stationZentras"
  | "stationGotthard"
  | "stationNsnw";

type SearchEngineCorpus = CrossModelParameterSpecification[];

const searchEngineCorpora = new Map<SearchEngineKey, SearchEngineCorpus>();
const searchEngines = new Map<SearchEngineKey, WeatherParamSearchParallel>();

function getOrCreateSearchEngine(key: SearchEngineKey): WeatherParamSearchParallel {
  const existingSearchEngine = searchEngines.get(key);
  if (existingSearchEngine) {
    return existingSearchEngine;
  }

  const searchEngine = new WeatherParamSearchParallel(searchOptions);
  const existingCorpus = searchEngineCorpora.get(key);
  if (existingCorpus) {
    searchEngine.replaceCorpus(existingCorpus);
  }
  searchEngines.set(key, searchEngine);
  return searchEngine;
}

function setSearchEngineCorpus(key: SearchEngineKey, corpus: SearchEngineCorpus) {
  searchEngineCorpora.set(key, corpus);
  const existingSearchEngine = searchEngines.get(key);
  if (existingSearchEngine) {
    existingSearchEngine.replaceCorpus(corpus);
  }
}

export function getStandardSearchEngine() {
  return getOrCreateSearchEngine("standard");
}

export function getStationObsSearchEngine() {
  return getOrCreateSearchEngine("stationObs");
}

export function getStationMosSearchEngine() {
  return getOrCreateSearchEngine("stationMos");
}

export function getStationAargauSearchEngine() {
  return getOrCreateSearchEngine("stationAargau");
}

export function getStationNationalstrassenSearchEngine() {
  return getOrCreateSearchEngine("stationNationalstrassen");
}

export function getStationZentrasSearchEngine() {
  return getOrCreateSearchEngine("stationZentras");
}

export function getStationGotthardSearchEngine() {
  return getOrCreateSearchEngine("stationGotthard");
}

export function getStationNsnwSearchEngine() {
  return getOrCreateSearchEngine("stationNsnw");
}

type SearchEnginesWeatherParams = {
  readonly standard: WeatherParamSearchParallel;
  readonly stationObs: WeatherParamSearchParallel;
  readonly stationMos: WeatherParamSearchParallel;
  readonly stationAargau: WeatherParamSearchParallel;
  readonly stationNationalstrassen: WeatherParamSearchParallel;
  readonly stationZentras: WeatherParamSearchParallel;
  readonly stationGotthard: WeatherParamSearchParallel;
  readonly stationNsnw: WeatherParamSearchParallel;
};

export const searchEnginesWeatherParams: SearchEnginesWeatherParams = {
  get standard() {
    return getStandardSearchEngine();
  },
  get stationObs() {
    return getStationObsSearchEngine();
  },
  get stationMos() {
    return getStationMosSearchEngine();
  },
  get stationAargau() {
    return getStationAargauSearchEngine();
  },
  get stationNationalstrassen() {
    return getStationNationalstrassenSearchEngine();
  },
  get stationZentras() {
    return getStationZentrasSearchEngine();
  },
  get stationGotthard() {
    return getStationGotthardSearchEngine();
  },
  get stationNsnw() {
    return getStationNsnwSearchEngine();
  },
};

export function getSearchEngine(layer: LayerUnionWithAll): WeatherParamSearchParallel {
  if (layer.kind === "StationLayerDescription") {
    // It needs type assertion to get it work bc MapLayer doesn't have model.
    if ((layer as any).model === STATION_MODEL_ID.MIX_OBS) {
      return getStationObsSearchEngine();
    }
    if ((layer as any).model === STATION_MODEL_ID.MM_MOS) {
      return getStationMosSearchEngine();
    }
    if ((layer as any).model === STATION_MODEL_ID.AARGAU) {
      return getStationAargauSearchEngine();
    }
    if ((layer as any).model === STATION_MODEL_ID.NATIONALSTRASSEN) {
      return getStationNationalstrassenSearchEngine();
    }
    if ((layer as any).model === STATION_MODEL_ID.ZENTRAS) {
      return getStationZentrasSearchEngine();
    }
    if ((layer as any).model === STATION_MODEL_ID.GOTTHARD) {
      return getStationGotthardSearchEngine();
    }
    if ((layer as any).model === STATION_MODEL_ID.NSNW) {
      return getStationNsnwSearchEngine();
    }
    logger.error(`Station layer currently does no support model ${(layer as any).model}`);
    return getStandardSearchEngine();
  }
  return getStandardSearchEngine();
}

// TODO-StationFeature:
// There is currently no endpoint for a list of station models. It is debatable if we should put those models together
// with the models for the regular parameters. If we use the model search engine purely to lookup a model regardless of
// the category of the models, it's probably ok to put them all in the same search engine.
// The model search engine is currently used everywhere in the code to lookup the ModelSchema object from a model indentifier string.
// For now, I put mix-obs and mm-mos in the same search engine as a quick fix.
export const searchEngineWeatherModels = new ModelSearchEngine();
// Completely made up ModelSchema for temporal use. This needs to be implemented in the api-layers backend and fetched in the code below.
export const stationObsModel_TEMPORAL = {
  identifier: STATION_MODEL_ID.MIX_OBS,
  name: "Station Observations",
  description_de: "<TEMPORAL TXT>",
  description_en: "<TEMPORAL TXT>",
  bounding_box: {
    resolution_lng: 0.000833333,
    resolution_lat: 0.000833333,
    south: -90,
    west: -180,
    north: 90,
    east: 180,
  },
};
export const stationMosModel_TEMPORAL = {
  identifier: STATION_MODEL_ID.MM_MOS,
  name: "Meteomatics MOS Prognoses",
  description_de: "<TEMPORAL TXT>",
  description_en: "<TEMPORAL TXT>",
  bounding_box: {
    resolution_lng: 0.000833333,
    resolution_lat: 0.000833333,
    south: -90,
    west: -180,
    north: 90,
    east: 180,
  },
};
export const stationModelAargau_TEMPORAL = {
  identifier: STATION_MODEL_ID.AARGAU,
  name: STATION_MODEL_LABEL.AARGAU,
  description_de: "Aargau Strassenstationen",
  description_en: "Aargau road stations",
  bounding_box: {
    resolution_lng: 0.000833333,
    resolution_lat: 0.000833333,
    south: -90, //can be changed to switzerland bbox?
    west: -180,
    north: 90,
    east: 180,
  },
};

export const stationModelNationalstrassen_TEMPORAL = {
  identifier: STATION_MODEL_ID.NATIONALSTRASSEN,
  name: STATION_MODEL_LABEL.NATIONALSTRASSEN,
  description_de: "ASTRA Strassenstationen",
  description_en: "ASTRA road stations",
  bounding_box: {
    resolution_lng: 0.000833333,
    resolution_lat: 0.000833333,
    south: -90,
    west: -180,
    north: 90,
    east: 180,
  },
};

export const stationModelZentras_TEMPORAL = {
  identifier: STATION_MODEL_ID.ZENTRAS,
  name: STATION_MODEL_LABEL.ZENTRAS,
  description_de: "Zentras Strassenstationen",
  description_en: "Zentras road stations",
  bounding_box: {
    resolution_lng: 0.000833333,
    resolution_lat: 0.000833333,
    south: -90,
    west: -180,
    north: 90,
    east: 180,
  },
};
export const stationModelGotthard_TEMPORAL = {
  identifier: STATION_MODEL_ID.GOTTHARD,
  name: STATION_MODEL_LABEL.GOTTHARD,
  description_de: "Gotthard Strassenstationen",
  description_en: "Gotthard road stations",
  bounding_box: {
    resolution_lng: 0.000833333,
    resolution_lat: 0.000833333,
    south: -90,
    west: -180,
    north: 90,
    east: 180,
  },
};

export const stationModelNsnw_TEMPORAL = {
  identifier: STATION_MODEL_ID.NSNW,
  name: STATION_MODEL_LABEL.NSNW,
  description_de: "NSNW Strassenstationen",
  description_en: "NSNW road stations",
  bounding_box: {
    resolution_lng: 0.000833333,
    resolution_lat: 0.000833333,
    south: -90,
    west: -180,
    north: 90,
    east: 180,
  },
};

export const stationModels_TEMPORAL = [stationObsModel_TEMPORAL, stationMosModel_TEMPORAL];

export const winterdienstStationModels_TEMPORAL = [
  stationModelAargau_TEMPORAL,
  stationModelNationalstrassen_TEMPORAL,
  stationModelZentras_TEMPORAL,
  stationModelGotthard_TEMPORAL,
  stationModelNsnw_TEMPORAL,
];

/**
 * Beware that the weather paramerters live in two places, in the context and the search engines.
 */
export const LayersApiContext = createContext<LayersApiResponses & { searchEngineReady: boolean }>({
  searchEngineReady: false,
});

export function useLayersApiContextValue(): LayersApiResponses & { searchEngineReady: boolean } {
  // Move searchEngineReady to state, as a part of authorization flow refactoring
  // Not sure how, but previous solution caused frozen UI state
  const [searchEngineReady, setSearchEngineReaady] = useState(false);
  const [layersApiCache, setLayersApiCache] = useState<LayersApiResponses>({});

  useEffect(() => {
    Promise.all([
      //   TODO api-layer point of failure
      layersApi.layerModelDescriptionApiV1LayersGet(),
      modelsApi.getModelsApiV1ModelsGet(),
      stationApi.layerModelDescriptionApiV1StationsObsGet(),
      stationApi.layerModelDescriptionApiV1StationsMosGet(),
    ])
      .then(([standardWeatherParams, modelsForWeatherParams, stationObsParams, stationMosParams]) => {
        setLayersApiCache({
          generalWeatherParams: standardWeatherParams,
          allSourceModelsGeneralWeatherParams: modelsForWeatherParams,
        });
        setSearchEngineCorpus("standard", standardWeatherParams);
        setSearchEngineCorpus("stationObs", stationObsParams);
        setSearchEngineCorpus("stationMos", stationMosParams);
        setSearchEngineCorpus("stationAargau", aargauRoadStationsParams);
        setSearchEngineCorpus("stationNationalstrassen", nationalstrassenRoadStationsParams);
        setSearchEngineCorpus("stationZentras", zentrasRoadStationsParams);
        setSearchEngineCorpus("stationGotthard", gotthardRoadStationsParams);
        setSearchEngineCorpus("stationNsnw", nsnwRoadStationsParams);

        // TODO-StationFeature
        // stationModels_TEMPORAL needs to be replaced once the backend provides the models for station.
        // Refer to where is is defined for further description.
        searchEngineWeatherModels.replaceCorpus([
          ...modelsForWeatherParams,
          ...stationModels_TEMPORAL,
          ...winterdienstStationModels_TEMPORAL,
        ]);
        setSearchEngineReaady(true);
      })
      .catch((_e) => {
        logger.error(_e);
        // TODO: show error in gui using the error dialog
        // setAppLoading("rejected");
      });
  }, []);
  return { ...layersApiCache, searchEngineReady };
}

/**
 * @returns An array of the models (data sources) for the standard weather parameters that are currently available.
 * Note: This is for getting a list of whole models. Please prefer to use usePartialWeatherParameterModels or useModel hooks, as they simply the steps for getting the model you want.
 */
export function useWeatherParamModels(): LayersApiResponses["allSourceModelsGeneralWeatherParams"] {
  return useContext(LayersApiContext).allSourceModelsGeneralWeatherParams;
}

/**
 * @returns An array of weather parameter description objects, each of them contains an array of available options and meta data.
 */
export function useWeatherParams(): LayersApiResponses["generalWeatherParams"] {
  return useContext(LayersApiContext).generalWeatherParams;
}

/**
 * @returns A boolean signifying if the search engine has already loaded.
 */
export function useSearchEngineReady() {
  const { searchEngineReady } = useContext(LayersApiContext);
  return searchEngineReady;
}
